/**
 * /admin/tenant-accounting — Super admin view of any tenant's accounting books.
 *
 * Read access is anytime + free. Any WRITE operation goes through the OTP-gated
 * edit-request flow: super admin proposes a change → tenant gets an email OTP
 * → tenant approves in their app → change commits + audit log + email all
 * tenant admins.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtDateTime } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";
import {
    BookOpen, ShieldCheck, Search, Plus, AlertTriangle, Mail, Clock,
    CheckCircle2, XCircle, FileText, BarChart3,
} from "lucide-react";

const fmtINR = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TABS = [
    { key: "tb",       label: "Trial Balance", icon: BarChart3 },
    { key: "pnl",      label: "P&L",           icon: BarChart3 },
    { key: "bs",       label: "Balance Sheet", icon: BarChart3 },
    { key: "journal",  label: "Journal",       icon: FileText },
    { key: "coa",      label: "Chart of A/Cs", icon: BookOpen },
    { key: "health",   label: "Health",        icon: ShieldCheck },
    { key: "edits",    label: "Edit requests", icon: Mail },
];

export default function AdminTenantAccounting() {
    const [tenants, setTenants] = useState([]);
    const [tid, setTid] = useState(null);
    const [tab, setTab] = useState("tb");
    const [proposeOpen, setProposeOpen] = useState(false);
    const [filter, setFilter] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        api.get("/admin/tenant-accounting/tenants").then(r => {
            setTenants(r.data);
            if (r.data.length && !tid) setTid(r.data[0].id);
        }).catch(e => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filtered = tenants.filter(t => !filter || t.name.toLowerCase().includes(filter.toLowerCase()));
    const current = useMemo(() => tenants.find(t => t.id === tid), [tenants, tid]);

    return (
        <div className="space-y-5" data-testid="admin-tenant-accounting">
            <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Super admin</div>
                <h1 className="text-3xl text-foreground">Tenant books</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    Read anytime. Any edit is OTP-gated through the tenant — they receive an email and must approve before the change commits.
                </p>
            </div>

            {tenants.length === 0 ? (
                <div className="border border-border bg-card p-8 text-center" data-testid="no-tenants-with-accounting">
                    <div className="text-muted-foreground">No tenants have the <span className="text-primary">accounting</span> feature flag enabled. Enable it on a tenant from the Feature flags page to view their books here.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
                    {/* Tenant picker */}
                    <div className="border border-border bg-card p-3 space-y-2">
                        <div className="flex items-center gap-2 border border-border bg-background px-2">
                            <Search className="w-3 h-3 text-muted-foreground"/>
                            <input value={filter} onChange={e => setFilter(e.target.value)}
                                placeholder="Search firms…"
                                data-testid="tenant-search"
                                className="bg-transparent outline-none text-xs text-foreground py-1.5 flex-1"/>
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{filtered.length} firms</div>
                        <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                            {filtered.map(t => (
                                <button key={t.id} onClick={() => setTid(t.id)}
                                    data-testid={`tenant-picker-${t.id}`}
                                    className={`w-full text-left px-2 py-1.5 border ${tid === t.id ? "border-primary bg-primary/5" : "border-transparent hover:border-border"}`}>
                                    <div className="text-xs text-foreground truncate">{t.name}</div>
                                    <div className="text-[10px] text-muted-foreground">{t.journal_count} entries</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Main viewer */}
                    <div className="space-y-3">
                        {current && (
                            <div className="flex flex-wrap items-center justify-between gap-3 border border-border bg-card px-4 py-3">
                                <div>
                                    <div className="text-xs text-muted-foreground uppercase tracking-[0.16em]">Viewing</div>
                                    <div className="text-lg text-foreground">{current.name}</div>
                                </div>
                                <button onClick={() => setProposeOpen(true)}
                                    data-testid="propose-edit-btn"
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em]">
                                    <Plus className="w-3 h-3"/>Propose edit (OTP to tenant)
                                </button>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-1 border-b border-border">
                            {TABS.map(t => {
                                const Ic = t.icon;
                                return (
                                    <button key={t.key} onClick={() => setTab(t.key)}
                                        data-testid={`admin-tab-${t.key}`}
                                        className={`flex items-center gap-1.5 px-3 py-2 text-[10px] uppercase tracking-[0.16em] border-b-2 ${tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                                        <Ic className="w-3 h-3"/>{t.label}
                                    </button>
                                );
                            })}
                        </div>

                        {tid && (
                            <>
                                {tab === "tb"      && <ReportPanel key={refreshKey} url={`/admin/tenant-accounting/${tid}/reports/trial-balance`} kind="tb"/>}
                                {tab === "pnl"     && <ReportPanel key={refreshKey} url={`/admin/tenant-accounting/${tid}/reports/pnl`} kind="pnl"/>}
                                {tab === "bs"      && <ReportPanel key={refreshKey} url={`/admin/tenant-accounting/${tid}/reports/balance-sheet`} kind="bs"/>}
                                {tab === "journal" && <JournalView key={refreshKey} tid={tid}/>}
                                {tab === "coa"     && <CoaView key={refreshKey} tid={tid}/>}
                                {tab === "health"  && <HealthView key={refreshKey} tid={tid}/>}
                                {tab === "edits"   && <EditsView key={refreshKey} tid={tid}/>}
                            </>
                        )}
                    </div>
                </div>
            )}

            {proposeOpen && tid && (
                <ProposeEditModal tid={tid}
                    onClose={() => setProposeOpen(false)}
                    onSubmitted={() => { setProposeOpen(false); setTab("edits"); setRefreshKey(k => k + 1); }}
                />
            )}
        </div>
    );
}

// --- panels ------------------------------------------------------------------
function ReportPanel({ url, kind }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get(url).then(r => setData(r.data)).catch(e => toast.error(apiError(e))); }, [url]);
    if (!data) return <div className="text-muted-foreground text-sm">Loading…</div>;
    if (kind === "tb") {
        return (
            <div className="border border-border bg-card" data-testid="admin-report-tb">
                <div className={`px-4 py-2 text-[10px] uppercase tracking-[0.16em] border-b border-border ${data.totals.balanced ? "text-emerald-400" : "text-rose-400"}`}>
                    {data.totals.balanced ? "✓ Balanced" : "✗ Unbalanced"} · {data.rows.length} accounts
                </div>
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border"><th className="text-left px-3 py-2">Code</th><th className="text-left px-3 py-2">Account</th><th className="text-right px-3 py-2">DR</th><th className="text-right px-3 py-2">CR</th><th className="text-right px-3 py-2">Balance</th></tr>
                    </thead>
                    <tbody>
                        {data.rows.map(r => (
                            <tr key={r.code} className="border-b border-card">
                                <td className="px-3 py-1.5 font-mono text-primary">{r.code}</td>
                                <td className="px-3 py-1.5 text-foreground">{r.name}</td>
                                <td className="px-3 py-1.5 text-right text-emerald-400 font-mono">{r.debit > 0 ? fmtINR(r.debit) : "—"}</td>
                                <td className="px-3 py-1.5 text-right text-rose-400 font-mono">{r.credit > 0 ? fmtINR(r.credit) : "—"}</td>
                                <td className="px-3 py-1.5 text-right text-foreground font-mono">{fmtINR(r.balance)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }
    if (kind === "pnl") {
        return (
            <div className="grid md:grid-cols-2 gap-3" data-testid="admin-report-pnl">
                <SimpleSection title="Income" rows={data.income} total={data.total_income} colorClass="text-emerald-400"/>
                <SimpleSection title="Expenses" rows={data.expense} total={data.total_expense} colorClass="text-rose-400"/>
                <div className="md:col-span-2 border border-primary/40 bg-primary/5 px-4 py-3 flex justify-between">
                    <span className="uppercase tracking-[0.16em] text-[10px] text-primary">Net Profit / Loss</span>
                    <span className={`font-mono ${data.net_profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtINR(data.net_profit)}</span>
                </div>
            </div>
        );
    }
    return (
        <div className="grid md:grid-cols-3 gap-3" data-testid="admin-report-bs">
            <SimpleSection title="Assets" rows={data.assets} total={data.total_assets} colorClass="text-emerald-400"/>
            <SimpleSection title="Liabilities" rows={data.liabilities} total={data.total_liabilities} colorClass="text-rose-400"/>
            <SimpleSection title="Equity" rows={data.equity} total={data.total_equity} colorClass="text-primary"/>
        </div>
    );
}

function SimpleSection({ title, rows, total, colorClass }) {
    return (
        <div className="border border-border bg-card overflow-x-auto">
            <div className="px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-b border-border">{title}</div>
            <table className="w-full text-xs">
                <tbody>
                    {(!rows || rows.length === 0) && <tr><td colSpan={2} className="px-3 py-4 text-center text-muted-foreground">No data</td></tr>}
                    {rows && rows.map(r => (
                        <tr key={r.code} className="border-b border-card">
                            <td className="px-3 py-1.5 text-foreground"><span className="font-mono text-muted-foreground mr-2">{r.code}</span>{r.name}</td>
                            <td className={`px-3 py-1.5 text-right font-mono ${colorClass}`}>{fmtINR(r.amount)}</td>
                        </tr>
                    ))}
                </tbody>
                <tfoot className="border-t-2 border-border">
                    <tr><td className="px-3 py-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Total</td>
                        <td className={`px-3 py-2 text-right font-mono ${colorClass}`}>{fmtINR(total)}</td></tr>
                </tfoot>
            </table>
        </div>
    );
}

function JournalView({ tid }) {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get(`/admin/tenant-accounting/${tid}/journal`).then(r => setRows(r.data)).catch(e => toast.error(apiError(e))); }, [tid]);
    const ctrl = useTableControls(rows, {
        accessors: { amount: (r) => (r.total_paise || 0) / 100 },
    });
    return (
        <div className="border border-border bg-card overflow-x-auto" data-testid="admin-journal-view">
            <FilterChip ctrl={ctrl} className="m-2"/>
            <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    <tr className="border-b border-border">
                        <SortableTh field="date" ctrl={ctrl} className="text-left px-3 py-2">Date</SortableTh>
                        <SortableTh field="narration" ctrl={ctrl} className="text-left px-3 py-2">Narration</SortableTh>
                        <SortableTh field="source" ctrl={ctrl} className="text-left px-3 py-2">Source</SortableTh>
                        <SortableTh field="amount" ctrl={ctrl} className="text-right px-3 py-2" align="right">Amount</SortableTh>
                    </tr>
                </thead>
                <tbody>
                    {ctrl.rows.length === 0 && <tr><td colSpan={4} className="px-3 py-8 text-center text-muted-foreground">No entries</td></tr>}
                    {ctrl.rows.map(r => (
                        <tr key={r.id} className="border-b border-card">
                            <td className="px-3 py-1.5 text-muted-foreground">{fmtDate(r.date)}</td>
                            <td className="px-3 py-1.5 text-foreground">{r.narration}</td>
                            <td className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{r.source}</td>
                            <td className="px-3 py-1.5 text-right text-foreground font-mono">{fmtINR((r.total_paise || 0) / 100)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CoaView({ tid }) {
    const [rows, setRows] = useState([]);
    useEffect(() => { api.get(`/admin/tenant-accounting/${tid}/coa`).then(r => setRows(r.data)).catch(e => toast.error(apiError(e))); }, [tid]);
    const ctrl = useTableControls(rows);
    return (
        <div className="border border-border bg-card overflow-x-auto" data-testid="admin-coa-view">
            <FilterChip ctrl={ctrl} className="m-2"/>
            <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    <tr className="border-b border-border">
                        <SortableTh field="code" ctrl={ctrl} className="text-left px-3 py-2">Code</SortableTh>
                        <SortableTh field="name" ctrl={ctrl} className="text-left px-3 py-2">Name</SortableTh>
                        <SortableTh field="type" ctrl={ctrl} className="text-left px-3 py-2">Type</SortableTh>
                        <SortableTh field="normal_balance" ctrl={ctrl} className="text-left px-3 py-2">Normal</SortableTh>
                    </tr>
                </thead>
                <tbody>
                    {ctrl.rows.map(r => (
                        <tr key={r.id} className="border-b border-card">
                            <td className="px-3 py-1.5 font-mono text-primary">{r.code}</td>
                            <td className="px-3 py-1.5 text-foreground">{r.name}</td>
                            <td className="px-3 py-1.5 text-[10px] uppercase text-muted-foreground">{r.type}</td>
                            <td className="px-3 py-1.5 text-[10px] uppercase text-muted-foreground">{r.normal_balance}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function HealthView({ tid }) {
    const [data, setData] = useState(null);
    useEffect(() => { api.get(`/admin/tenant-accounting/${tid}/health`).then(r => setData(r.data)).catch(e => toast.error(apiError(e))); }, [tid]);
    if (!data) return <div className="text-muted-foreground text-sm">Loading…</div>;
    return (
        <div className="space-y-2" data-testid="admin-health-view">
            {data.findings.map((f, i) => (
                <div key={f.code || i} className="border border-border bg-card px-4 py-3 flex items-start gap-3">
                    {f.severity === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5"/> : <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5"/>}
                    <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{f.code}</div>
                        <div className="text-sm text-foreground">{f.message}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function EditsView({ tid }) {
    const [rows, setRows] = useState([]);
    const reload = useCallback(() => api.get(`/admin/tenant-accounting/${tid}/edits`).then(r => setRows(r.data)).catch(e => toast.error(apiError(e))), [tid]);
    useEffect(() => { reload(); }, [reload]);
    const cancel = async (id) => {
        if (!window.confirm("Cancel this pending edit request? Tenant will no longer be able to approve it.")) return;
        try { await api.post(`/admin/tenant-accounting/edit-requests/${id}/cancel`); toast.success("Cancelled"); reload(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const STATUS = {
        pending:   { col: "text-amber-400", Ic: Clock,         label: "Pending tenant approval" },
        applied:   { col: "text-emerald-400", Ic: CheckCircle2, label: "Applied" },
        rejected:  { col: "text-rose-400",  Ic: XCircle,       label: "Rejected by tenant" },
        cancelled: { col: "text-muted-foreground", Ic: XCircle,       label: "Cancelled by super-admin" },
        expired:   { col: "text-muted-foreground", Ic: Clock,         label: "Expired (24h)" },
        blocked_too_many_otp_attempts: { col: "text-rose-400", Ic: XCircle, label: "Blocked (too many wrong OTPs)" },
    };
    return (
        <div className="space-y-2" data-testid="admin-edits-view">
            {rows.length === 0 && <div className="border border-border bg-card px-4 py-8 text-center text-muted-foreground">No edit requests yet</div>}
            {rows.map(r => {
                const s = STATUS[r.status] || { col: "text-muted-foreground", Ic: Clock, label: r.status };
                const Ic = s.Ic;
                return (
                    <div key={r.id} className="border border-border bg-card px-4 py-3" data-testid={`admin-edit-row-${r.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                                <div className={`text-[10px] uppercase tracking-[0.16em] flex items-center gap-1.5 ${s.col}`}>
                                    <Ic className="w-3 h-3"/>{s.label}
                                </div>
                                <div className="text-sm text-foreground mt-1">{r.summary}</div>
                                <div className="text-[10px] text-muted-foreground mt-1">Reason: {r.reason}</div>
                                <div className="text-[10px] text-muted-foreground mt-2">
                                    Requested {fmtDateTime(r.requested_at)} · OTP to <span className="text-primary">{r.owner_email}</span>
                                    {r.applied_at && ` · applied ${fmtDateTime(r.applied_at)}`}
                                </div>
                            </div>
                            {r.status === "pending" && (
                                <button onClick={() => cancel(r.id)} data-testid={`admin-cancel-edit-${r.id}`}
                                    className="text-[10px] uppercase tracking-[0.16em] text-rose-400 hover:underline">Cancel</button>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// --- propose edit modal ------------------------------------------------------
function ProposeEditModal({ tid, onClose, onSubmitted }) {
    const [op, setOp] = useState("post_journal");
    const [reason, setReason] = useState("");
    const [narration, setNarration] = useState("");
    const [lines, setLines] = useState([
        { account_code: "", debit: "", credit: "", memo: "" },
        { account_code: "", debit: "", credit: "", memo: "" },
    ]);
    const [entryId, setEntryId] = useState("");
    const [periodKey, setPeriodKey] = useState("");
    const [coa, setCoa] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => { api.get(`/admin/tenant-accounting/${tid}/coa`).then(r => setCoa(r.data)).catch(() => {}); }, [tid]);

    const setLine = (i, k, v) => { const next = [...lines]; next[i] = { ...next[i], [k]: v }; setLines(next); };
    const addLine = () => setLines([...lines, { account_code: "", debit: "", credit: "", memo: "" }]);

    const totals = useMemo(() => {
        const dr = lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
        const cr = lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
        return { dr, cr, balanced: Math.abs(dr - cr) < 0.01 && dr > 0 };
    }, [lines]);

    const submit = async (e) => {
        e.preventDefault();
        if (reason.trim().length < 5) { toast.error("Please give a clear reason (5+ chars) — tenant will see this in their approval email"); return; }
        let payload = {};
        if (op === "post_journal") {
            if (!totals.balanced) { toast.error("Entry not balanced"); return; }
            payload = { narration,
                lines: lines.filter(l => l.account_code).map(l => ({
                    account_code: l.account_code,
                    debit: parseFloat(l.debit) || 0,
                    credit: parseFloat(l.credit) || 0,
                    memo: l.memo || undefined,
                })) };
        } else if (op === "reverse_entry") {
            if (!entryId) { toast.error("Enter the journal entry id to reverse"); return; }
            payload = { entry_id: entryId };
        } else {
            if (!periodKey.match(/^\d{4}-\d{2}$/)) { toast.error("Period must be YYYY-MM"); return; }
            payload = { period_key: periodKey };
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/tenant-accounting/${tid}/edit-requests`,
                { operation: op, payload, reason });
            toast.success(`Edit request sent · OTP emailed to ${data.otp_sent_to} · expires in 24h`);
            onSubmitted();
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-3xl p-5 space-y-4 max-h-[90vh] overflow-y-auto"
                data-testid="propose-edit-modal">
                <div>
                    <h3 className="text-lg text-foreground">Propose an edit · OTP-gated</h3>
                    <p className="text-[10px] text-muted-foreground mt-1">An email with a 6-digit OTP will be sent to the firm owner. The change only commits when the tenant enters that OTP.</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Operation">
                        <select value={op} onChange={e => setOp(e.target.value)}
                            data-testid="propose-op"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm">
                            <option value="post_journal">Post journal entry</option>
                            <option value="reverse_entry">Reverse a journal entry</option>
                            <option value="close_period">Close a period</option>
                            <option value="reopen_period">Re-open a period</option>
                        </select>
                    </Field>
                </div>
                <Field label="Reason (visible to tenant in approval email)">
                    <textarea value={reason} onChange={e => setReason(e.target.value)} required minLength={5} maxLength={500}
                        data-testid="propose-reason"
                        placeholder="E.g. Annual audit correction — Mar 2026 bank charges were missed in original posting"
                        rows={2}
                        className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                </Field>

                {op === "post_journal" && (
                    <>
                        <Field label="Narration">
                            <input value={narration} onChange={e => setNarration(e.target.value)}
                                className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                        </Field>
                        <div className="space-y-1">
                            {lines.map((ln, i) => (
                                <div key={i} className="grid grid-cols-12 gap-2">
                                    <select value={ln.account_code} onChange={e => setLine(i, "account_code", e.target.value)}
                                        className="col-span-4 bg-background border border-border px-2 py-1.5 text-foreground text-xs">
                                        <option value="">Account…</option>
                                        {coa.filter(a => a.is_active).map(a => <option key={a.id} value={a.code}>{a.code} · {a.name}</option>)}
                                    </select>
                                    <input value={ln.debit} onChange={e => setLine(i, "debit", e.target.value)}
                                        placeholder="DR ₹" type="number" step="0.01"
                                        className="col-span-3 bg-background border border-border px-2 py-1.5 text-emerald-400 text-xs text-right"/>
                                    <input value={ln.credit} onChange={e => setLine(i, "credit", e.target.value)}
                                        placeholder="CR ₹" type="number" step="0.01"
                                        className="col-span-3 bg-background border border-border px-2 py-1.5 text-rose-400 text-xs text-right"/>
                                    <input value={ln.memo} onChange={e => setLine(i, "memo", e.target.value)}
                                        placeholder="Memo"
                                        className="col-span-2 bg-background border border-border px-2 py-1.5 text-foreground text-xs"/>
                                </div>
                            ))}
                            <button type="button" onClick={addLine} className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline">+ add line</button>
                        </div>
                        <div className="text-xs">
                            <span className="text-emerald-400">DR {fmtINR(totals.dr)}</span>
                            <span className="text-muted-foreground mx-2">·</span>
                            <span className="text-rose-400">CR {fmtINR(totals.cr)}</span>
                            <span className="ml-3">{totals.balanced ? <span className="text-emerald-400">✓</span> : <span className="text-rose-400">✗</span>}</span>
                        </div>
                    </>
                )}
                {op === "reverse_entry" && (
                    <Field label="Journal entry ID to reverse">
                        <input value={entryId} onChange={e => setEntryId(e.target.value)} required
                            placeholder="6a2ac1b0bc8102ea82e116c7"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono"/>
                    </Field>
                )}
                {(op === "close_period" || op === "reopen_period") && (
                    <Field label="Period (YYYY-MM)">
                        <input value={periodKey} onChange={e => setPeriodKey(e.target.value)} required
                            placeholder="2026-03"
                            className="w-40 bg-background border border-border px-3 py-2 text-foreground text-sm font-mono"/>
                    </Field>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-card">
                    <button type="button" onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                    <button type="submit" disabled={saving}
                        data-testid="propose-submit"
                        className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                        {saving ? "Sending OTP…" : "Send OTP to tenant"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            {children}
        </div>
    );
}
